import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { GetMembership as GetFacilityMembership, TFacilityMembership } from "api/rpc/2024-04/facilityAdmin/membership/membership";
import { GetMembership as GetClientMembership } from "api/rpc/2024-04/clientAdmin/membership/membership";

import Sheet from "components/sheet/Sheet";
import DataTable from "components/dataTable/DataTable";
import Search from "components/search/Search";
import { useAppDispatch } from "hooks/redux";
import { searchHistoryClear } from "redux/actions/searchBars/searchHistory";
import { valueToString } from "helpers/Helpers";

type AttachMembershipModalProps = {
  isVisible: boolean;
  onCancel: () => void;
  onOk: (membership: TFacilityMembership) => void;
  excludedMembershipIds?: number[];
  allowedMembershipTypes?: Array<"membership" | "add_on">;
  userLevel?: "facility" | "client";
};

const TABLE_LIMIT = 25;

function formatMembershipType(type?: string) {
  if (!type) {
    return "";
  }

  return valueToString(type.replace(/-/g, "_"));
}

export default function AttachMembershipModal(props: AttachMembershipModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [memberships, setMemberships] = useState<TFacilityMembership[]>(null);
  const [filters, setFilters] = useState({
    search: "",
    searching: false,
    offset: 0,
  });

  useEffect(() => {
    if (!props.isVisible) {
      return;
    }

    const source = axios.CancelToken.source();
    void searchMemberships(source.token);

    return () => {
      source.cancel();
    };
  }, [props.isVisible, filters.search, filters.offset, props.allowedMembershipTypes]);

  async function searchMemberships(token: CancelToken) {
    setMemberships(null);
    const GetMembership = props.userLevel === "client" ? GetClientMembership : GetFacilityMembership;
    const { status, data } = await GetMembership(
      {
        search: filters.search,
        archived: false,
        limit: TABLE_LIMIT,
        offset: filters.offset,
      } as { search?: string },
      false,
      token,
    );

    if (status === StatusCode.OK) {
      const allowedTypes = props.allowedMembershipTypes ?? ["membership"];
      setMemberships(
        ((data ?? []) as TFacilityMembership[]).filter(
          membership =>
            allowedTypes.includes(membership.membership_type as "membership" | "add_on") &&
            !props.excludedMembershipIds?.includes(membership.id),
        ),
      );
    }
  }

  return (
    <Sheet
      open={props.isVisible}
      size="medium"
      closable
      title={t("Attach membership")}
      onCancel={() => {
        dispatch(searchHistoryClear());
        props.onCancel();
      }}
      onOk={() => undefined}
      okText={t("Close")}
    >
      <Search
        searchCallback={searchValue => setFilters(prev => ({ ...prev, search: searchValue, offset: 0 }))}
        historyKey="attach-group-membership"
      />
      <DataTable
        columns={[
          { label: t("Title") },
          { label: t("Type") },
        ]}
        loading={memberships === null}
      >
        {memberships?.map(membership => (
          <tr key={membership.id} onClick={() => props.onOk(membership)} style={{ cursor: "pointer" }}>
            <td>{membership.title}</td>
            <td>{formatMembershipType(membership.type)}</td>
          </tr>
        ))}
      </DataTable>
    </Sheet>
  );
}
